/**
 * Shared cost-type → procurement category map.
 *
 * The Materials / Rentals / Subcontractors / Other tabs each declare their own
 * local `tDescriptions` object. The Uncommitted and Dates views need to reason
 * about all four sets at once (a buyer works across categories, not tabs), so
 * the codes live here in one place.
 *
 * Keep the per-tab `tDescriptions` in sync with these groups — the tabs use
 * theirs to drive tab-local filtering and this file is the cross-tab view.
 */

export const RENTALS = {
    '52200': 'Outside Equipment Rental',
};

export const MATERIALS = {
    '53100': 'Materials',
    '53200': 'Mats',
    '53210': 'Mats - Purchase',
    '53300': 'Small Tools – Purchase',
    '53310': 'Small Tools – Rent',
    '53400': 'Skids',
    '53500': 'Consumables',
};

export const SUBCONTRACTORS = {
    '54100': 'Internal Subcontractor',
    '54200': 'External Subcontractor',
    '54300': 'Hauling/Transportation',
};

export const SERVICES = {
    '51800': 'Field Bonus',
    '52300': 'Fuel',
    '52400': 'Repairs',
    '55100': 'Personal Meals',
    '55200': 'Team Meals/Entertain',
    '55300': 'Airfare',
    '55400': 'Lodging',
    '55900': 'Other Travel',
    '56100': 'Field Office Rent',
    '56200': 'Field Office Utility',
    '56300': 'Field Office Expense',
    '56400': 'Lab Fees & Testing',
    '56500': 'Safety & PPE',
    '56600': 'Bonds and Insurance Premiums',
    '56700': 'Permits',
    '56800': 'Insurance Claims',
    '57100': 'Taxes',
    '57200': 'Accrued Expense',
    '57300': 'Warranty Reserve',
    '57900': 'Miscellaneous',
};

// Display order matters — buyers scan Rentals and Subs first.
export const CATEGORIES = ['Rentals', 'Subcontractors', 'Materials', 'Services'];

const GROUPS = {
    Rentals: RENTALS,
    Subcontractors: SUBCONTRACTORS,
    Materials: MATERIALS,
    Services: SERVICES,
};

/** code → category name, e.g. '52200' → 'Rentals' */
export const COST_TYPE_CATEGORY = Object.entries(GROUPS).reduce((acc, [category, codes]) => {
    Object.keys(codes).forEach((code) => { acc[code] = category; });
    return acc;
}, {});

/** code → human description, e.g. '52200' → 'Outside Equipment Rental' */
export const COST_TYPE_DESCRIPTION = Object.values(GROUPS).reduce(
    (acc, codes) => Object.assign(acc, codes),
    {}
);

/** Every cost type the two procurement views cover. */
export const ALL_COST_TYPES = Object.keys(COST_TYPE_CATEGORY);

/**
 * Category for a cost type code. Returns '' for codes outside the four groups
 * (labor, equipment-owned, etc.) so callers can filter them out explicitly
 * rather than silently bucketing them somewhere wrong.
 */
export const categoryOf = (costTypeCode) => {
    if (costTypeCode === null || costTypeCode === undefined) return '';
    return COST_TYPE_CATEGORY[String(costTypeCode).trim()] || '';
};

/** Description for a cost type code, falling back to the code itself. */
export const describeCostType = (costTypeCode) => {
    const code = String(costTypeCode ?? '').trim();
    return COST_TYPE_DESCRIPTION[code] || code;
};
